/* eslint-disable no-unused-vars */
import useLatestCallback from "@shared/hooks/useLatestCallback";
import { PlayerSettings, SEEK_STATUS, Source, SpeedRate } from "@types";
import { DEFAULT_SETTINGS, parseVTT } from "@utils/player";
import React, {
  MouseEvent,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  useReducer,
} from "react";
import { isServer, useIsMount } from "@utils/helpers";
import { usePlayerContext } from "@context/player";
import * as Browser from "bowser";
import { ControlsElement } from "../controls";
import { Cue, PlayerElement } from "..";
import { useUI } from "@context/ui-managed-context";
import { useSingleAndDoubleClick } from "@shared/hooks/useSingleAndDoubleClick";
import { useMediaQuery } from "@shared/hooks/useMediaQuery";
import useOrientation from "@shared/hooks/useOrientation";
import { useRouter } from "next/router";
import useDebounceValue from "@shared/hooks/useDebounceValue";
import { modalActions, useModals } from "@context/modals";
import { MODAL_TYPES } from "types/modals";

type BrowserStatus = {
  isFirefox: boolean;
  isMobile: boolean;
  isIPhone: boolean;
};

type keyHandlersProps = {
  Space: (e?: MouseEvent<HTMLElement> | KeyboardEvent) => Promise<void>;
  KeyM: () => void;
  KeyF: () => Promise<void>;
  KeyP: () => Promise<void>;
  ArrowRight: () => void;
  ArrowLeft: () => void;
  ArrowUp: () => void;
  ArrowDown: () => void;
};
interface Props {
  ref: React.ForwardedRef<PlayerElement>;
  sources: Source[];
  nextLessonName?: string;
  theaterMode: boolean;
  subtitle?: string;
  startFrom?: number;
  curVideoId?: number | string;
  timeUpdateHandler?: (
    time: number,
    duration: number,
    seek: boolean
  ) => Promise<void>;
  onEnd?: (time: number, duration: number) => Promise<void>;
  onToggleTheaterHandler?: () => void;
  autoPlayOnSrcChange: boolean;
  stepId?: number;
  onPlay?: () => void;
  defaultMuted?: boolean;
  autoPlayOnMount?: boolean;
}

const useManageVideo = ({
  ref,
  sources,
  nextLessonName,
  theaterMode,
  subtitle,
  startFrom,
  curVideoId,
  timeUpdateHandler,
  onEnd,
  onToggleTheaterHandler,
  autoPlayOnSrcChange,
  stepId,
  onPlay,
  defaultMuted = false,
  autoPlayOnMount = false,
}: Props) => {
  const isMount = useIsMount();
  const { playingVideoId, setPlayingVideoId } = usePlayerContext();

  const [id] = useState(
    stepId ? String(stepId) : curVideoId ? String(curVideoId) : null
  );
  const [cues, setCues] = useState<Cue[]>([]);
  const [currentText, setCurrentText] = useState("");

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const videoContainerRef = useRef<HTMLDivElement | null>(null);
  const controlsRef = useRef<ControlsElement | null>(null);
  const timeoutHandle = useRef<NodeJS.Timeout>(undefined);
  const animateHandle = useRef<NodeJS.Timeout>(undefined);

  useEffect(() => {
    if (subtitle && subtitle !== null)
      fetch(subtitle)
        .then((res) => res.text())
        .then(parseVTT)
        .then(setCues)
        .catch((error) => {
          console.log(error);
        });
  }, [subtitle]);

  const onEnterFullscreen = () => {
    setSettings((prev) => ({ ...prev, fullScreen: true }));
  };

  const onExitFullscreen = () => {
    videoTimeUpdateHandler();
    setSettings((prev) =>
      prev.fullScreen ? { ...prev, fullScreen: false } : prev
    );
  };
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      const currentTime = video.currentTime;
      const cue = cues.find(
        (c) => currentTime >= c.start && currentTime <= c.end
      );
      setCurrentText(cue?.text ?? "");
    };

    video.addEventListener("timeupdate", handleTimeUpdate);
    video.addEventListener("webkitbeginfullscreen", onEnterFullscreen);
    video.addEventListener("webkitendfullscreen", onExitFullscreen);
    return () => {
      video.removeEventListener("timeupdate", handleTimeUpdate);
      video.removeEventListener("webkitbeginfullscreen", onEnterFullscreen);
      video.removeEventListener("webkitendfullscreen", onExitFullscreen);
    };
  }, [cues, videoRef]);
  ///first config
  const reloadMeta = useRef({
    from: 0,
    wasPlaying: false,
    changed: false,
    reload: false,
    startFrom: -1,
  });

  const seekMeta = useRef({
    wasPlaying: false,
    reload: false,
  });
  const everPlayedVideo = useRef(false);

  const [showNext, setShowNext] = useState(false);
  const setBrowserConfig = () => {
    if (isServer())
      return { isFirefox: false, isMobile: false, isIPhone: false };

    const browserDetail = Browser.getParser(window.navigator.userAgent);
    const isMobile = browserDetail?.getPlatformType(true) !== "desktop";
    const isFirefox = browserDetail?.satisfies({ firefox: ">0" }) ?? false;
    const isIPhone = browserDetail?.getOSName() === "iOS";
    return {
      isFirefox,
      isMobile,
      isIPhone,
    };
  };
  const [browser, setBrowser] = useState<BrowserStatus>(setBrowserConfig());
  const { query } = useRouter();

  const defaultQuality = ["480", "720", "1080"].includes(
    String(query.quality)?.replace("p", "")
  )
    ? String(query.quality)?.replace("p", "") + "p"
    : "720p";

  const [settings, setSettings] = useState<PlayerSettings>({
    ...DEFAULT_SETTINGS,
    pipSupported: !browser.isFirefox,
    withSubtitle: Boolean(subtitle?.length),
    isMuted: defaultMuted,
    quality: defaultQuality,
  });

  const [trackedDefaultMuted, setTrackedDefaultMuted] = useState(defaultMuted);
  if (defaultMuted !== trackedDefaultMuted) {
    setTrackedDefaultMuted(defaultMuted);
    if (defaultMuted) {
      setSettings((prev) => (prev.isMuted ? prev : { ...prev, isMuted: true }));
    }
  }

  const [position, setPosition] = useState<{
    show: boolean;
    x: number | null;
    y: number | null;
  }>({ show: false, x: null, y: null });
  const [showVideoControls, setShowVideoControls] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const modals = useModals((s) => s.modals);
  const [isPlaying, setIsPlaying] = useState<boolean>();
  ///detect video play before or not
  const [isPlayed, setIsPLayed] = useState(false);

  ///show control  video
  const [isControlsVisible, setIsControlsVisible] = useState<boolean>();
  const [keepPauseVideo, setKeepPauseVideo] = useState(false);
  const [seekAnimate, setSeekAnimate] = useState<SEEK_STATUS>();
  const [videoSources, setSources] = useState<Source[]>(sources || []);
  const [showMobileSpeedRateList, toggleMobileSpeedRateList] = useReducer(
    (prev) => !prev,
    false
  );
  const [showMobileQualityList, toggleMobileQualityList] = useReducer(
    (prev) => !prev,
    false
  );

  const [animatePlay, setAnimatePlay] = useState(false);
  const debouncedAnimatePlay = useDebounceValue(animatePlay, 100) as boolean;

  const menuRef = useRef<HTMLDivElement | null>(null);

  const { isMobile, isIPhone } = browser;

  const { speedRate, volume, quality } = settings;

  const onTimeUpdateCallback = useLatestCallback(timeUpdateHandler);

  useEffect(() => {
    if (playingVideoId !== id && isPlaying) {
      videoRef.current?.pause();
      setIsPlaying(false);
    }
  }, [id, playingVideoId, isPlaying]);

  useEffect(() => {
    checkPlayPictureInPicture();
  }, [playingVideoId]);

  useEffect(() => {
    setSettings((prev) => ({
      ...prev,
      withSubtitle: Boolean(subtitle?.length),
    }));
  }, [subtitle]);

  ///handle player with keyboard key
  const keyboardShortcuts = useLatestCallback((e: KeyboardEvent) => {
    if (!videoRef.current) return;
    if (
      modals.length &&
      !modals.some((mod) => mod.type === MODAL_TYPES.VIDEO_PLAYER)
    )
      return;
    if (
      document.activeElement?.tagName === "INPUT" ||
      document.activeElement?.tagName === "TEXTAREA" ||
      (document.activeElement?.tagName === "DIV" &&
        document.activeElement.classList.contains("ql-editor"))
    )
      return;
    showControls();
    e.preventDefault();
    const video = videoRef.current;
    video.focus();
    const { code } = e;
    const keyHandlers: keyHandlersProps = {
      Space: togglePlayHandler,
      KeyM: toggleMuteHandler,
      KeyF: toggleFullscreenHandler,
      KeyP: togglePipHandler,
      ArrowRight: () => backOrForWardHandler(false),
      ArrowLeft: () => backOrForWardHandler(true),
      ArrowUp: () => updateVolumeWithKeyboard(1),
      ArrowDown: () => updateVolumeWithKeyboard(0),
    };
    const handler = keyHandlers[code as keyof keyHandlersProps];

    if (handler) handler(e);
  });

  const animatePlayback = useCallback(() => {
    setAnimatePlay(true);
    if (animateHandle.current) clearTimeout(animateHandle.current);
    animateHandle.current = setTimeout(() => {
      setAnimatePlay(false);
    }, 500);
  }, []);

  //check picture in picture and stop and disable
  const checkPlayPictureInPicture = async () => {
    const video = videoRef.current;
    try {
      if (video === document.pictureInPictureElement) {
        setSettings((prev) => ({
          ...prev,
          pipEnable: false,
        }));
        await document.exitPictureInPicture();
        setSettings((prev) => ({
          ...prev,
          pip: false,
        }));
      }
    } catch (error) {
    } finally {
      setSettings((prev) => ({
        ...prev,
        pipEnable: true,
      }));
    }
  };

  const togglePlayHandler = useCallback(
    async (e?: MouseEvent<HTMLElement | SVGSVGElement> | KeyboardEvent) => {
      const video = videoRef.current;
      if (isLoading) return;
      if (video && !Boolean(isIPhone)) {
        // setIsControlsVisible(true);
        showControls();
        animatePlayback();
        const isPaused = Boolean(video.paused || video.ended);
        setPlayingVideoId(id);
        if (isPaused || keepPauseVideo) {
          if (keepPauseVideo) setKeepPauseVideo(false);
          video.pause();
          ///check if enable  playing with picture in picture should be disable
          checkPlayPictureInPicture();
          video
            .play()
            .then(() => {
              // pause another video in this page
              const videoElements = document.querySelectorAll("video");
              videoElements?.forEach((videoItem) => {
                if (videoItem !== video && videoItem.played) {
                  videoItem.pause();
                }
              });
            })
            .catch((error) => {
              console.log(error);
            });
        } else {
          video.pause();
          showControls();
          setPlayingVideoId((prev) => (prev === id ? null : prev));
        }
      }
      (e?.target as HTMLElement)?.blur();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [animatePlayback, isLoading, keepPauseVideo]
  );
  const pausePlayer = () => {
    const video = videoRef.current;
    if (video) {
      video.pause();
      showControls();
      setPlayingVideoId((prev) => (prev === id ? null : prev));
    }
  };
  const togglePlayByClickHandler = useCallback(
    async (e?: MouseEvent<HTMLElement | SVGSVGElement> | KeyboardEvent) => {
      setKeepPauseVideo(false);
      togglePlayHandler?.(e);
    },
    [togglePlayHandler]
  );

  useImperativeHandle(
    ref,
    () => ({
      togglePlayHandler,
      pausePlayer,
    }),
    [togglePlayHandler, pausePlayer]
  );
  const rightClickHandler = (e: globalThis.MouseEvent) => {
    if (
      videoContainerRef.current &&
      !videoContainerRef.current.contains(e.target as Node)
    )
      return;
    e.preventDefault();
    if (!videoContainerRef.current) return;
    const rect = videoContainerRef?.current.getBoundingClientRect();

    const left = e.clientX - rect.left;
    const top = e.clientY - rect.top;
    const videoBoundHeight = rect?.height ?? 0;
    const videoBoundWidth = rect?.width ?? 0;
    const menuHeight = 320;
    const menuWidth = 192;
    const x =
      left + menuWidth < videoBoundWidth
        ? left
        : videoBoundWidth - (menuWidth + 20);

    const y =
      top + menuHeight > videoBoundHeight ? videoBoundHeight - menuHeight : top;

    setPosition({ show: true, x, y });
  };
  const handleClickOutside = (e: globalThis.MouseEvent) => {
    if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
      setPosition((prev) => ({ ...prev, show: false }));
    }
  };
  const fullScreenChange = () => {
    if (
      !(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      )
    ) {
      videoTimeUpdateHandler();
      setSettings((prev) =>
        prev.fullScreen
          ? {
              ...prev,
              fullScreen: false,
            }
          : prev
      );
    }
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (
      document.activeElement?.tagName === "INPUT" ||
      document.activeElement?.tagName === "TEXTAREA" ||
      (document.activeElement?.tagName === "DIV" &&
        document.activeElement?.classList.contains("ql-editor"))
    )
      return;
    if (
      e.keyCode === 32 || //space
      e.keyCode === 38 || //arrow up
      e.keyCode === 40 //arrow
    ) {
      e.preventDefault();
    }
  };

  const onContentLoaded = () => {
    if (!("pictureInPictureEnabled" in document)) {
      setSettings((prev) => ({
        ...prev,
        pipSupported: false,
      }));
    }
  };

  useEffect(() => {
    onContentLoaded();
    ///change fullscreen with esc
    fullScreenChange();
    document.onmouseup = () => (document.onmousemove = null);

    document.addEventListener("keyup", keyboardShortcuts);
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("DOMContentLoaded", onContentLoaded);
    document.addEventListener("fullscreenchange", fullScreenChange);
    document.addEventListener(
      "contextmenu",
      rightClickHandler as EventListener
    );
    document.addEventListener("mousedown", handleClickOutside as EventListener);

    return () => {
      document.removeEventListener("keyup", keyboardShortcuts);
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("DOMContentLoaded", onContentLoaded);
      document.removeEventListener("fullscreenchange", fullScreenChange);
      document.removeEventListener(
        "contextmenu",
        rightClickHandler as EventListener
      );
      document.removeEventListener(
        "mousedown",
        handleClickOutside as EventListener
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyboardShortcuts]);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = speedRate;
    }
  }, [speedRate]);

  useEffect(() => {
    if (!videoRef.current) return;
    videoRef.current.volume = volume;
  }, [volume]);

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      reloadMeta.current.wasPlaying = false;
      const playing = !video.paused;
      if (playing) {
        video.pause();
        setPlayingVideoId((prev) => (prev === id ? null : prev));
      }
      reloadMeta.current = {
        from: 0,
        startFrom: startFrom!,
        wasPlaying: Boolean(autoPlayOnSrcChange || playing || isPlaying),
        changed: true,
        reload: reloadMeta.current.reload,
      };
      setSources(sources);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sources]);

  useEffect(() => {
    !isMount && videoTimeUpdateHandler();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theaterMode]);

  ///exit picture in picture
  const exitPip = async () => {
    if (document.pictureInPictureElement) {
      await document.exitPictureInPicture();
    }
  };

  useEffect(() => {
    const supportsOrientationChange = "onorientationchange" in window;
    const orientationEvent = supportsOrientationChange
      ? "orientationchange"
      : "resize";

    const updateSize = () => {
      const browserConfig = setBrowserConfig();
      setBrowser(browserConfig);
      exitPip();
    };

    window.addEventListener(orientationEvent, updateSize);
    return () => {
      window.removeEventListener(orientationEvent, updateSize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (videoRef.current && isPlaying !== undefined) {
      const video = videoRef.current;
      const playing = !video.paused;
      if (playing) {
        video.pause();
        setPlayingVideoId((prev) => (prev === id ? null : prev));
      }
      reloadMeta.current = {
        from: video.currentTime,
        startFrom: -1,
        wasPlaying: playing,
        changed: true,
        reload: reloadMeta.current.reload,
      };
      setSources([
        ...videoSources.filter((f) => f.label === settings.quality),
        ...videoSources.filter((f) => f.label !== settings.quality),
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quality]);

  const startPlayer = () => {
    if (videoRef.current && isPlaying !== undefined) {
      const video = videoRef.current;
      const playing = !video.paused;
      if (playing) {
        video.pause();
        setPlayingVideoId((prev) => (prev === id ? null : prev));
      }
      videoRef.current.currentTime = 0;
      videoRef.current.play();
      setPlayingVideoId(id);
    }
  };

  useEffect(() => {
    if (reloadMeta.current.changed) {
      if (videoRef.current) {
        const video = videoRef.current;
        reloadMeta.current.reload = true;
        video.load();
        video.focus();
        ///set speed rate in video
        video.playbackRate = settings.speedRate;
        video.volume = volume;
        if (reloadMeta.current.wasPlaying) {
          video
            .play()
            .then(() => {
              setPlayingVideoId(id);
            })
            .catch((error) => {
              console.log(error);
            });
        }
      }
      reloadMeta.current.changed = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoSources]);

  const replayAfterSeek = () => {
    if (seekMeta.current.reload && videoRef.current?.readyState === 4) {
      if (seekMeta.current.wasPlaying) {
        videoRef.current?.play();
        setPlayingVideoId(id);
      }
      seekMeta.current.reload = false;
    }
  };

  const clearTimeoutHandler = () => {
    if (timeoutHandle.current) {
      clearTimeout(timeoutHandle.current);
      timeoutHandle.current = undefined;
    }
  };

  const showControls = () => {
    if (isPlaying === undefined) return;
    clearTimeoutHandler();
    setIsControlsVisible(true);
    timeoutHandle.current = setTimeout(() => {
      setIsControlsVisible(false);
    }, 2_500);
  };

  const hideControls = () => {
    if (everPlayedVideo.current) {
      clearTimeoutHandler();
      setIsControlsVisible(false);
    }
  };

  const setVideoVolume = (prev: PlayerSettings, status: 0 | 1) => {
    //status=0 minus volume and status=1 plus volume , volume range (0,1) step 0.1
    if (status) {
      // If status is 1 (unmute)
      if (prev.isMuted) {
        return 0; // Unmute from mute sets volume to 0
      } else if (prev.volume <= 0.9) {
        return prev.volume + 0.1; // Increase volume by 0.1, max 1.0
      } else if (prev.volume > 0.9) {
        return 1;
      }
    } else {
      // If status is 0 (mute)
      if (prev.volume >= 0.1) {
        return prev.volume - 0.1; // Decrease volume by 0.1, min 0.0
      } else {
        return 0;
      }
    }

    // No change in volume
    return prev.volume;
  };

  const setAndCheckMutedVideo = (prev: PlayerSettings, status: 0 | 1) => {
    if (status) {
      if (prev.isMuted) {
        return false;
      } else if (prev.volume <= 0.9) {
        return false;
      } else if (prev.volume > 0.9) {
        return false;
      } else {
        return true;
      }
    } else {
      if (prev.volume >= 0.1) {
        return prev.isMuted;
      }
    }
    return prev.isMuted;
  };

  const updateVolumeWithKeyboard = (status: 1 | 0) => {
    setSettings((prev) => ({
      ...prev,
      volume: setVideoVolume(prev, status),
      isMuted: setAndCheckMutedVideo(prev, status),
    }));
  };

  const volumeChangeHandler = (volume: number) => {
    setSettings((prev) => ({
      ...prev,
      volume: volume,
      lastVolume: prev.volume,
      isMuted: prev.isMuted ? false : prev.isMuted,
    }));
  };

  useEffect(() => {
    if (!defaultMuted || !videoRef.current) return;

    videoRef.current.muted = true;
    if (!autoPlayOnMount) {
      videoRef.current.pause();
      setKeepPauseVideo(true);
    }
  }, [defaultMuted, autoPlayOnMount]);

  const speedRateChangeHandler = (speed: SpeedRate) => {
    showControls();
    if (videoRef.current) videoRef.current.playbackRate = speed;
    setSettings((prev) => ({
      ...prev,
      speedRate: speed,
    }));
  };

  const qualityChangeHandler = (quality: string) => {
    showControls();
    setSettings((prev) => ({
      ...prev,
      quality: quality,
    }));
  };

  const toggleTheaterHandler = async () => {
    if (!videoContainerRef.current) return;
    onToggleTheaterHandler?.();
  };

  const toggleFullscreenHandler = async () => {
    //check fullscreen video player
    if (!videoContainerRef.current) return;

    try {
      setSettings((prev) => ({
        ...prev,
        fullScreenEnable: false,
      }));

      const isFullscreen =
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).mozFullScreenElement ||
        (document as any).msFullscreenElement;

      if (isFullscreen) {
        const exitFullscreenFn =
          document.exitFullscreen ||
          (document as any).mozCancelFullScreen ||
          (document as any).webkitExitFullscreen ||
          (document as any).msExitFullscreen;

        if (exitFullscreenFn) {
          await exitFullscreenFn.call(document);
        }

        setSettings((prev) => ({
          ...prev,
          fullScreen: false,
        }));
      } else {
        const requestFullscreenFn =
          videoContainerRef.current.requestFullscreen ||
          (videoContainerRef.current as any).mozRequestFullScreen ||
          (videoContainerRef.current as any).webkitRequestFullscreen ||
          (videoContainerRef.current as any).msRequestFullscreen;

        if (requestFullscreenFn) {
          await requestFullscreenFn.call(videoContainerRef.current);
        }

        setSettings((prev) => ({
          ...prev,
          fullScreen: true,
        }));
      }
    } catch (error) {
      console.error(error);
    } finally {
      videoTimeUpdateHandler();
      setSettings((prev) => ({
        ...prev,
        fullScreenEnable: true,
      }));
    }
  };

  //toggle picture in picture
  const togglePipHandler = async () => {
    const video = videoRef.current;
    if (!video) return;
    try {
      if (video !== document.pictureInPictureElement) {
        setSettings((prev) => ({
          ...prev,
          pipEnable: false,
        }));
        await video.requestPictureInPicture();
        setSettings((prev) => ({
          ...prev,
          pip: true,
        }));
      } else {
        await document.exitPictureInPicture();
        setSettings((prev) => ({
          ...prev,
          pip: false,
        }));
      }
    } catch (error) {
      console.error(error);
    } finally {
      setSettings((prev) => ({
        ...prev,
        pipEnable: true,
      }));
    }
  };

  const canPlayHandler = () => {
    if (videoRef.current) {
      setShowVideoControls(!videoRef.current.canPlayType);
      replayAfterSeek();
    }
  };

  const playHandler = () => {
    if (keepPauseVideo) return;
    setPlayingVideoId(id);
    if (onPlay) onPlay();
    setIsPlaying(true);
    setIsPLayed(true);
    if (!everPlayedVideo.current) everPlayedVideo.current = true;
  };

  const pauseHandler = () => {
    if (!seekMeta.current.reload) setIsPlaying(false);
    setPlayingVideoId((prev) => (prev === id ? null : prev));
    if (Boolean(isIPhone)) {
      videoRef.current?.pause();
    }
  };

  const jumpToSpeceficTime = async (secs: number, affectVolume?: boolean) => {
    if (videoRef.current) {
      const playing = !videoRef.current.paused;
      if (playing) {
        videoRef.current.pause();
        pauseHandler();
      }
      videoRef.current.currentTime = secs;
      try {
        if (affectVolume) toggleMuteHandler(false);
        await videoRef.current.play();
        playHandler();
      } catch (err) {
        if (affectVolume) toggleMuteHandler(true);
        await videoRef.current.play();
        playHandler();
      }
    }
  };

  const loadMetaDataHandler = () => {
    if (videoRef.current) {
      const video = videoRef.current;
      const from =
        reloadMeta.current.startFrom > -1
          ? (reloadMeta.current.startFrom * video.duration) / 100
          : reloadMeta.current.from;

      if (reloadMeta.current.reload && from < video.duration) {
        video.currentTime = from;
        video.playbackRate = speedRate;
      }
      reloadMeta.current.reload = false;

      replayAfterSeek();

      const videoDuration = Math.round(video.duration);
      controlsRef.current?.updateDuration?.(videoDuration);
    }
  };

  const betweenTimeRange = (time: number, min: number, max: number) =>
    time >= min && time <= max;

  const videoTimeUpdateHandler = () => {
    if (videoRef.current) {
      const video = videoRef.current;

      const next =
        video.duration && video.currentTime
          ? betweenTimeRange(video.duration - video.currentTime, 0, 3)
          : false;

      setShowNext(next);
      if (next && isPlayed) setIsControlsVisible(true);
      onTimeUpdateCallback(video.currentTime, video.duration, false);
      controlsRef.current?.updateProgress?.(video.currentTime, video.duration);

      if (video.buffered.length > 0) {
        controlsRef.current?.updateBuffer?.(
          video.buffered.end(0),
          video.duration
        );
      }
      if (!seekMeta.current.reload) setIsLoading(false);
    }
  };

  const videoVolumeChangeHandler = () => {
    if (videoRef.current) {
      const video = videoRef.current;
      setSettings((prev) => ({
        ...prev,
        volume: video.volume,
      }));
    }
  };

  const toggleMuteHandler = (sepecficVal?: boolean) => {
    setSettings((prev) => {
      const newVal =
        typeof sepecficVal === "boolean" ? sepecficVal : !prev.isMuted;
      return {
        ...prev,
        volume: !newVal ? prev.lastVolume : 0,
        isMuted: newVal,
        lastVolume: newVal ? prev.volume : prev.lastVolume,
      };
    });
  };

  const toggleSubtitle = () => {
    setSettings((prev) => ({
      ...prev,
      subtitleOn: !prev.subtitleOn,
    }));
  };

  const videoEndedHandler = () => {
    if (videoRef.current && isPlayed) {
      setShowNext(false);
      controlsRef.current?.videoEnded?.(!!nextLessonName);
      onEnd?.(videoRef.current.duration, videoRef.current.duration);
    }
  };

  const autoPlayOnMountHandler = videoRef.current
    ? async () => {
        const v = videoRef.current;
        if (!v) return;
        else {
          setPlayingVideoId(id);
          try {
            toggleMuteHandler(false);
            await v.play();
            playHandler();
          } catch (err) {
            console.log("Autoplay error", err);
            toggleMuteHandler(true);
            await v.play();
            playHandler();
          }
        }
      }
    : null;

  const mouseMoveHandler = () => {
    if (!videoRef.current) return;
    showControls();
  };

  const backOrForWardHandler = (backward: boolean) => {
    showControls();
    if (videoRef.current) {
      const video = videoRef.current;
      const time = backward ? video.currentTime - 10 : video.currentTime + 10;
      video.currentTime = Math.floor(time);
      controlsRef.current?.updateProgress?.(time, video.duration);
    }
  };

  const handleSeekTo = useCallback(
    (time: number) => {
      if (!videoRef.current) return;
      const video = videoRef.current;
      const playing = !video.paused;
      if (playing) {
        video.pause();
        setIsLoading(true);
        seekMeta.current.wasPlaying = playing;
        seekMeta.current.reload = true;
      }
      video.currentTime = isNaN(time) ? 0 : time;
      controlsRef.current?.updateProgress?.(
        video.currentTime,
        video.duration,
        time
      );
      onTimeUpdateCallback(time, video.duration, true);
    },
    [onTimeUpdateCallback]
  );

  const videoClickVideoHandler = (
    e: MouseEvent<HTMLElement> | KeyboardEvent
  ) => {
    if (isMobile && !videoRef.current?.paused)
      return setIsControlsVisible(true);
    return togglePlayByClickHandler(e);
  };

  const videoDoubleClickHandler = (e: MouseEvent<HTMLVideoElement>) => {
    if (videoRef.current) {
      const videoWidth = videoRef.current.offsetWidth;

      if (e.nativeEvent.offsetX < videoWidth / 2) {
        backOrForWardHandler(true);
        setSeekAnimate(SEEK_STATUS.BACKWARD);
      } else {
        backOrForWardHandler(false);
        setSeekAnimate(SEEK_STATUS.FORWARD);
      }
      setTimeout(() => {
        setSeekAnimate(undefined);
      }, 750);
    }
  };

  const handleClick = useSingleAndDoubleClick(
    videoClickVideoHandler,
    videoDoubleClickHandler
  );
  const videoH = videoRef.current?.clientHeight || 1;
  const videoW = videoRef.current?.clientWidth || 1;
  const containerH = controlsRef.current?.container?.clientHeight;
  const isFS = settings.fullScreen;
  const isVisible = isControlsVisible;
  const orientation = useOrientation();
  const isLandscapeMobile =
    useMediaQuery("(min-width:576px) and (max-width:992px)") &&
    orientation === "landscape";
  const calcHeight = () => {
    if (isVisible) {
      if (!isFS || isLandscapeMobile || !isMobile) return containerH;
      return (videoH - (videoW * 9) / 16) / 2;
    } else {
      if (isFS && isMobile && !isLandscapeMobile)
        return (videoH - (videoW * 9) / 16) / 2;
      return 20;
    }
  };
  const closeMenu = () => {
    setPosition({ show: false, x: null, y: null });
  };

  return {
    videoContainerRef,
    animatePlay,
    debouncedAnimatePlay,
    mouseMoveHandler,
    videoRef,
    settings,
    browser,
    isMobile,
    isIPhone,
    showVideoControls,
    togglePlayByClickHandler,
    playHandler,
    pauseHandler,
    loadMetaDataHandler,
    videoTimeUpdateHandler,
    videoVolumeChangeHandler,
    replayAfterSeek,
    videoEndedHandler,
    autoPlayOnMountHandler,
    canPlayHandler,
    setIsLoading,
    subtitle,
    videoSources,
    isPlaying,
    controlsRef,
    isLoading,
    theaterMode,
    isControlsVisible,
    volumeChangeHandler,
    qualityChangeHandler,
    speedRateChangeHandler,
    toggleFullscreenHandler,
    toggleTheaterHandler,
    togglePipHandler,
    toggleSubtitle,
    toggleMuteHandler,
    handleSeekTo,
    backOrForWardHandler,
    nextLessonName,
    showNext,
    handleClick,
    seekAnimate,
    showMobileSpeedRateList,
    toggleMobileSpeedRateList,
    showMobileQualityList,
    toggleMobileQualityList,
    showControls,
    hideControls,
    jumpToSpeceficTime,
    currentText,
    calcHeight,
    position,
    menuRef,
    startPlayer,
    closeMenu,
  };
};
export default useManageVideo;
